/**
 * Nidus-Integrations-API für Autopilot, Version 1 (docs/autopilot/04).
 * Präfix: /integrations/autopilot/v1
 *
 * WICHTIG: Diese Datei ist die Vertragsquelle für BEIDE Repositories. In Nidus liegt
 * eine byte-identische Kopie unter apps/platform-api/src/modules/autopilot-integration/contract.ts.
 * Änderungen immer in beiden Repos gleichzeitig vornehmen; der Contract-Test
 * (contract-sync.test.ts / scripts/contract-hash) prüft die Prüfsumme.
 */
import { z } from 'zod';
import { AktiverStatus, Assignment, AuthorizationType, IsoDateTime, Score, Trigger, Uuid, VorgangStatus, } from './common.js';
export const NIDUS_API_PREFIX = '/integrations/autopilot/v1';
export const NIDUS_API_VERSION = '1';
/** Version des kanonischen Hashformats für contentRevision/sourceManifestHash/Antwort-Hash. */
export const HASH_FORMAT_VERSION = 'v1';
/** App-Rollen der Maschinenidentität (04 „Sicherheit“). */
export const NidusServiceRole = z.enum([
    'Autopilot.Read',
    'Autopilot.Execute',
    'Autopilot.ManageProfiles',
    'Autopilot.ManagePolicies',
]);
// ---------------------------------------------------------------------------
// Capabilities & Kataloge
// ---------------------------------------------------------------------------
export const Capabilities = z.object({
    instanceId: z.string().min(1),
    instanceName: z.string(),
    apiVersion: z.literal(NIDUS_API_VERSION),
    hashFormatVersion: z.literal(HASH_FORMAT_VERSION),
    contextSchemaVersion: z.string(),
    /** z. B. selections, context, knowledge-search, resolve-operations, automation-policy, execution-gate, metrics */
    features: z.array(z.string()),
    grantedRoles: z.array(NidusServiceRole),
    /** Gesellschaften, auf die die Serviceidentität begrenzt ist. */
    gesellschaftScope: z.array(Uuid),
    limits: z.object({
        pageSizeDefault: z.number().int(),
        pageSizeMax: z.number().int(),
        knowledgeSearchLimitMax: z.number().int(),
        answerMaxChars: z.number().int(),
        selectionTtlHours: z.number().int(),
        minScheduledTicketAgeMinutes: z.number().int(),
    }),
    delivery: z.object({
        channels: z.array(z.enum(['EMAIL', 'PORTAL'])),
        /** true, wenn der Versandprovider Idempotenz bzw. Statusabfrage unterstützt. */
        providerIdempotency: z.boolean(),
        providerStatusQuery: z.boolean(),
    }),
    csat: z.object({ available: z.boolean() }),
});
export const Gesellschaft = z.object({ id: Uuid, name: z.string(), kurzname: z.string().nullable() });
export const Vorgangstyp = z.object({
    id: Uuid,
    code: z.string(),
    name: z.string(),
    /** Version des Typkatalogs (versionierter Nidus-Katalog, 02 „Auswahl“). */
    katalogVersion: z.string(),
    aktiv: z.boolean(),
});
export const Catalogs = z.object({
    instanceId: z.string(),
    gesellschaften: z.array(Gesellschaft),
    vorgangstypen: z.array(Vorgangstyp),
    aktiveStatus: z.array(AktiverStatus),
});
export const ObjektRef = z.object({ id: Uuid, gesellschaftId: Uuid, bezeichnung: z.string() });
// ---------------------------------------------------------------------------
// Ticket-Snapshot & Auswahl
// ---------------------------------------------------------------------------
export const FocusReservation = z.object({
    active: z.boolean(),
    /** Nur ob eine menschliche Reservierung aktiv ist – keine Namen notwendig. */
    until: IsoDateTime.nullable(),
});
export const TicketSnapshot = z.object({
    id: Uuid,
    nummer: z.string(),
    betreff: z.string().nullable(),
    gesellschaftId: Uuid,
    objektId: Uuid.nullable(),
    vorgangstypId: Uuid.nullable(),
    status: VorgangStatus,
    verantwortlicherProfilId: Uuid.nullable(),
    verantwortlicherName: z.string().nullable(),
    rowVersion: z.number().int(),
    /** Opaque, kanonisch gehashte Kontextrevision (fachlicher Stand ohne Lese-/Fokus-Zeitstempel). */
    contextRevision: z.string(),
    createdAt: IsoDateTime,
    focusReservation: FocusReservation,
});
export const SelectionFilter = z.object({
    /** Leer = alle berechtigten Gesellschaften. */
    gesellschaftIds: z.array(Uuid).default([]),
    /** Leer + includeUntyped=false = alle Typen inkl. „Ohne Typ“. */
    vorgangstypIds: z.array(Uuid).default([]),
    /** Nur relevant, wenn vorgangstypIds nicht leer ist: „Ohne Typ“ zusätzlich einschließen. */
    includeUntyped: z.boolean().default(false),
    /** Leer = alle fünf aktiven Status. */
    statuses: z.array(AktiverStatus).default([]),
    /** UTC, inklusiv. Vom Autopilot-Backend aus Europe/Berlin-Kalendertagen berechnet. */
    createdFrom: IsoDateTime.nullable().default(null),
    /** UTC, exklusiv. */
    createdToExclusive: IsoDateTime.nullable().default(null),
    assignment: Assignment.default('ALL'),
});
export const ScheduledSelectionAuth = z.object({
    gesellschaftId: Uuid,
    policyVersion: z.number().int().positive(),
    scheduleRunId: Uuid,
});
export const CreateSelectionRequest = z
    .object({
    trigger: Trigger,
    filter: SelectionFilter,
    /** Stand der gespiegelten Negativliste, gegen den Nidus prüft. */
    exclusionPolicyVersion: z.number().int().nonnegative(),
    scheduled: ScheduledSelectionAuth.optional(),
    correlation: z.object({ batchId: Uuid }),
})
    .superRefine((v, ctx) => {
    if (v.trigger === 'SCHEDULED') {
        if (!v.scheduled)
            ctx.addIssue({ code: 'custom', message: 'scheduled erforderlich bei SCHEDULED' });
        else if (v.filter.gesellschaftIds.length !== 1 || v.filter.gesellschaftIds[0] !== v.scheduled.gesellschaftId)
            ctx.addIssue({ code: 'custom', message: 'SCHEDULED verlangt genau die Policy-Gesellschaft' });
    }
    else if (v.scheduled) {
        ctx.addIssue({ code: 'custom', message: 'scheduled nur bei SCHEDULED zulässig' });
    }
});
export const SelectionStatus = z.enum(['BUILDING', 'READY', 'FAILED']);
export const SelectionCounts = z.object({
    /** Autorisierter Rohbestand. Partition: raw = excludedByNegativeList + incomplete + usable (02). */
    raw: z.number().int(),
    excludedByNegativeList: z.number().int(),
    incomplete: z.number().int(),
    usable: z.number().int(),
    /** Verschiedene nichtleere objektId im nutzbaren Bestand. */
    distinctObjects: z.number().int(),
    withoutObject: z.number().int(),
    /** Verteilung im nutzbaren Bestand; vorgangstypId null = „Ohne Typ“. */
    byType: z.array(z.object({ vorgangstypId: Uuid.nullable(), count: z.number().int() })),
});
export const Selection = z.object({
    id: Uuid,
    instanceId: z.string(),
    status: SelectionStatus,
    trigger: Trigger,
    filter: SelectionFilter,
    createdAt: IsoDateTime,
    expiresAt: IsoDateTime,
    /** Nidus-Serverzeit beim Materialisieren. */
    selectionAsOf: IsoDateTime.nullable(),
    /** Nur SCHEDULED: selectionAsOf − 30 min; Bedingung createdAt <= createdAtLte. */
    createdAtLte: IsoDateTime.nullable(),
    ageFilterApplied: z.boolean(),
    policyVersion: z.number().int().nullable(),
    scheduleRunId: Uuid.nullable(),
    exclusionPolicyVersion: z.number().int(),
    counts: SelectionCounts.nullable(),
    error: z.object({ code: z.string(), message: z.string() }).nullable(),
});
export const SelectionOutcome = z.enum(['USABLE', 'EXCLUDED_NEGATIVE', 'INCOMPLETE']);
export const ExclusionHit = z.object({
    term: z.string(),
    sourceType: z.enum(['BETREFF', 'BESCHREIBUNG', 'NACHRICHT', 'ANHANG']),
    sourceId: z.string().nullable(),
});
export const SelectionTicket = TicketSnapshot.extend({
    outcome: SelectionOutcome,
    exclusionHits: z.array(ExclusionHit),
    incompleteReasons: z.array(z.string()),
});
// ---------------------------------------------------------------------------
// Kontext & Quellen
// ---------------------------------------------------------------------------
export const SourceType = z.enum([
    'ANFRAGE',
    'NACHRICHT',
    'NOTIZ',
    'ANHANG',
    'OBJEKT',
    'EINHEIT',
    'VERTRAG',
    'MELDER',
    'FINANZ',
    'WISSEN',
    'PRAEZEDENZ',
    'LEITPLANKE',
]);
export const Visibility = z.enum(['EXTERN', 'INTERN']);
export const ExtractionStatus = z.enum([
    'OK',
    'NOT_APPLICABLE',
    'PENDING',
    'FAILED',
    'UNSUPPORTED',
    'NOT_CLEAN',
]);
export const Source = z.object({
    sourceId: z.string(),
    type: SourceType,
    version: z.string(),
    contentHash: z.string(),
    gesellschaftId: Uuid,
    objektId: Uuid.nullable(),
    visibility: Visibility,
    title: z.string().nullable(),
    /** Nur bei Nachrichten: Richtung. */
    direction: z.enum(['EINGEHEND', 'AUSGEHEND']).nullable(),
    /** Fachlicher Stand / Stichtag. */
    effectiveAt: IsoDateTime.nullable(),
    retrievedAt: IsoDateTime,
    /** Fachliches Gültigkeitsende; null + validityUnknown=false = unbegrenzt. */
    validUntil: IsoDateTime.nullable(),
    validityUnknown: z.boolean(),
    extractionStatus: ExtractionStatus,
    content: z.string().nullable(),
    /** Wenn content nicht inline geliefert wird: über GET /sources/{id}/versions/{version} ladbar. */
    contentRef: z.string().nullable(),
});
export const Completeness = z.enum(['COMPLETE', 'PARTIAL', 'FAILED']);
/** Typisierte Lücke statt stiller null (01 „Kontextmethoden fangen Fehler ab“). */
export const ContextIssue = z.object({
    code: z.enum(['NOT_PRESENT', 'NOT_AUTHORIZED', 'RETRIEVAL_FAILED', 'EXTRACTION_FAILED', 'TRUNCATED', 'NOT_CLEAN']),
    sourceType: SourceType,
    sourceId: z.string().nullable(),
    /** true, wenn die Quelle für eine Beantwortung Pflicht ist (→ nicht ausführbar). */
    required: z.boolean(),
    message: z.string(),
});
export const Eligibility = z.object({
    eligible: z.boolean(),
    code: z.enum(['NO_RECIPIENT', 'DELIVERY_SUPPRESSED']).nullable(),
    reason: z.string().nullable(),
});
export const TicketContext = z.object({
    instanceId: z.string(),
    ticket: TicketSnapshot,
    /** Stabiler fachlicher Teil der Kontextrevision (08 §5 Analyse-Fingerprint). */
    contentRevision: z.string(),
    /** sha256 über sortierte (sourceId, version, contentHash, validUntil, visibility); ohne retrievedAt. */
    sourceManifestHash: z.string(),
    hashFormatVersion: z.literal(HASH_FORMAT_VERSION),
    completeness: Completeness,
    issues: z.array(ContextIssue),
    sources: z.array(Source),
    recipient: Eligibility.extend({
        recipients: z.array(z.object({ kind: z.enum(['MELDER', 'PORTAL']), addressHash: z.string(), displayName: z.string().nullable() })),
    }),
    delivery: Eligibility,
    /** Frühestes validUntil aller Quellen. */
    validUntil: IsoDateTime.nullable(),
    retrievedAt: IsoDateTime,
});
export const KnowledgeSearchRequest = z.object({
    query: z.string().min(1).max(4000),
    sourceTypes: z.array(z.enum(['WISSEN', 'PRAEZEDENZ', 'VERTRAG', 'LEITPLANKE'])).min(1),
    limit: z.number().int().min(1).max(20).default(10),
});
export const RetrievalUsage = z.object({
    provider: z.string(),
    model: z.string(),
    requestId: z.string().nullable(),
    inputTokens: z.number().int().nullable(),
});
export const KnowledgeSearchResponse = z.object({
    instanceId: z.string(),
    results: z.array(Source.extend({ relevance: z.number().min(0).max(1) })),
    /** Nidus-interne kostenpflichtige Embedding-Nutzung dieses Aufrufs; null = keine. */
    usage: z.array(RetrievalUsage),
});
export const SourceVersion = Source.extend({
    scanStatus: z.enum(['CLEAN', 'PENDING', 'INFECTED', 'FEHLER', 'NOT_APPLICABLE']),
});
// ---------------------------------------------------------------------------
// Bearbeiterprofile
// ---------------------------------------------------------------------------
export const NidusAgent = z.object({
    id: Uuid,
    kontoId: Uuid,
    profilId: Uuid,
    displayName: z.string(),
    signature: z.string().nullable(),
    active: z.boolean(),
    allowedGesellschaftIds: z.array(Uuid),
    rowVersion: z.number().int(),
    externalRef: z.string(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
export const CreateAgentRequest = z.object({
    /** Stabile Autopilot-ID des agent_profile. */
    externalRef: z.string().min(1).max(100),
    displayName: z.string().min(2).max(120),
    signature: z.string().max(2000).nullable(),
    allowedGesellschaftIds: z.array(Uuid).min(1),
});
export const UpdateAgentRequest = z.object({
    expectedRowVersion: z.number().int(),
    displayName: z.string().min(2).max(120).optional(),
    signature: z.string().max(2000).nullable().optional(),
    active: z.boolean().optional(),
    allowedGesellschaftIds: z.array(Uuid).min(1).optional(),
});
// ---------------------------------------------------------------------------
// Kennzahlen (08 §6)
// ---------------------------------------------------------------------------
export const CsatNotImplemented = z.object({
    availability: z.literal('NOT_IMPLEMENTED'),
    value: z.null(),
    sampleSize: z.null(),
    period: z.null(),
});
export const CompanyMetrics = z.object({
    instanceId: z.string(),
    items: z.array(z.object({
        gesellschaftId: Uuid,
        backlog: z.object({
            availability: z.enum(['AVAILABLE', 'ERROR']),
            /** Nie 0 bei Fehler: null + availability=ERROR. */
            count: z.number().int().nullable(),
            asOf: IsoDateTime,
        }),
        csat: CsatNotImplemented,
    })),
});
// ---------------------------------------------------------------------------
// Policy-, Gate- und Negativlisten-Spiegel (04, 08 §4)
// ---------------------------------------------------------------------------
export const AutomationPolicyMirror = z.object({
    enabled: z.boolean(),
    /** Monoton; gleiche Version + gleicher Body = idempotent; sonst 409 VERSION_CONFLICT. */
    policyVersion: z.number().int().positive(),
    authorizationHash: z.string(),
    minTicketAgeMinutes: z.number().int().min(30),
    generator: z.object({ modelConfigVersionId: Uuid, provider: z.string(), modelId: z.string() }),
    reviewProfileVersionId: Uuid,
    thresholds: z.object({ minCorrectness: Score, maxErrorRisk: Score }),
    activatedBy: z.string(),
});
export const AutomationPolicyState = AutomationPolicyMirror.extend({
    gesellschaftId: Uuid,
    confirmedAt: IsoDateTime,
    instanceId: z.string(),
});
export const ExecutionGate = z.object({ version: z.number().int().positive(), enabled: z.boolean() });
export const ExecutionGateState = ExecutionGate.extend({ confirmedAt: IsoDateTime, instanceId: z.string() });
export const ExclusionPolicy = z.object({
    version: z.number().int().positive(),
    /** Bereits NFKC-normalisierte, deutsch kleingeschriebene, deduplizierte Begriffe. */
    terms: z.array(z.string().min(1)),
});
export const ExclusionPolicyState = ExclusionPolicy.extend({ confirmedAt: IsoDateTime, instanceId: z.string() });
// ---------------------------------------------------------------------------
// Ausführung (04 „Schreiboperation“)
// ---------------------------------------------------------------------------
export const ManualAuthorization = z.object({
    authorizationType: z.literal('MANUAL_APPROVAL'),
    trigger: z.literal('MANUAL'),
    authorization: z.object({ approverUserId: Uuid, approvalId: Uuid }),
});
export const ScheduledAuthorization = z.object({
    authorizationType: z.literal('COMPANY_POLICY'),
    trigger: z.literal('SCHEDULED'),
    authorization: z.object({
        gesellschaftId: Uuid,
        policyVersion: z.number().int().positive(),
        scheduleRunId: Uuid,
        authorizationHash: z.string(),
    }),
});
const ResolveOperationBody = z.object({
    expected: z.object({
        rowVersion: z.number().int(),
        status: AktiverStatus,
        verantwortlicherProfilId: Uuid.nullable(),
        contextRevision: z.string(),
        sourceManifestHash: z.string(),
    }),
    agentProfilId: Uuid,
    answer: z.object({ text: z.string().min(1).max(20000), sha256: z.string() }),
    exclusionPolicyVersion: z.number().int().nonnegative(),
    correlation: z.object({ batchId: Uuid, executionRunId: Uuid, proposalVersion: z.number().int().positive() }),
});
export const ResolveOperationRequest = z.discriminatedUnion('authorizationType', [
    ResolveOperationBody.extend(ManualAuthorization.shape),
    ResolveOperationBody.extend(ScheduledAuthorization.shape),
]);
export const OperationStatus = z.enum([
    /** Phase A abgewiesen (Konflikt/Gate); kein Schreibzugriff. Replay liefert dieselbe Ablehnung. */
    'REJECTED',
    /** Phase A erfolgreich, Versandauftrag angelegt. */
    'PENDING_DELIVERY',
    'SUCCEEDED',
    'FAILED',
    'UNKNOWN',
    'SENT_NOT_RESOLVED',
]);
export const DeliveryStatus = z.enum(['PENDING', 'SENT', 'FAILED', 'UNKNOWN', 'SUPPRESSED', 'NO_RECIPIENT']);
export const Delivery = z.object({
    deliveryId: Uuid,
    recipientHash: z.string(),
    channel: z.enum(['EMAIL', 'PORTAL']),
    status: DeliveryStatus,
    contentSha256: z.string().nullable(),
    providerOperationId: z.string().nullable(),
    attempts: z.number().int(),
    lastAttemptAt: IsoDateTime.nullable(),
    errorCode: z.string().nullable(),
});
export const ResolveOperation = z.object({
    operationId: Uuid,
    instanceId: z.string(),
    idempotencyKey: z.string(),
    ticketId: Uuid,
    messageId: Uuid.nullable(),
    agentProfilId: Uuid,
    trigger: Trigger,
    authorizationType: AuthorizationType,
    status: OperationStatus,
    /** Aggregat über deliveries. */
    deliveryStatus: DeliveryStatus.nullable(),
    deliveries: z.array(Delivery),
    answerSha256: z.string(),
    /** Hash des tatsächlich versandten Artefakts (inkl. Signatur). */
    sentArtifactSha256: z.string().nullable(),
    newRowVersion: z.number().int().nullable(),
    errorCode: z.string().nullable(),
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
    completedAt: IsoDateTime.nullable(),
});
/** Terminale Operationsstatus aus Sicht von Autopilot (UNKNOWN ist NICHT terminal). */
export const TERMINAL_OPERATION_STATUS = ['REJECTED', 'SUCCEEDED', 'FAILED', 'SENT_NOT_RESOLVED'];
export const NIDUS_ROUTES = [
    { method: 'GET', path: '/capabilities', summary: 'Instanzidentität und Fähigkeiten', role: 'Autopilot.Read', response: Capabilities },
    { method: 'GET', path: '/catalogs', summary: 'Berechtigte Gesellschaften, Vorgangstypen, Status', role: 'Autopilot.Read', response: Catalogs },
    { method: 'GET', path: '/catalogs/objects', summary: 'Objekte (paginiert, ?gesellschaftId=)', role: 'Autopilot.Read', response: z.object({ items: z.array(ObjektRef), nextCursor: z.string().nullable() }) },
    { method: 'POST', path: '/selections', summary: 'Auswahl materialisieren (202)', role: 'Autopilot.Read', idempotent: true, request: CreateSelectionRequest, response: Selection, status: 202 },
    { method: 'GET', path: '/selections/{id}', summary: 'Auswahlstatus und Zählungen', role: 'Autopilot.Read', response: Selection },
    { method: 'GET', path: '/selections/{id}/tickets', summary: 'Festgeschriebene Tickets (Cursor)', role: 'Autopilot.Read', response: z.object({ items: z.array(SelectionTicket), nextCursor: z.string().nullable() }) },
    { method: 'GET', path: '/tickets/{id}/context', summary: 'Versionierter Ticketkontext (rein lesend)', role: 'Autopilot.Read', response: TicketContext },
    { method: 'POST', path: '/tickets/{id}/knowledge-search', summary: 'Autorisierte Wissens-/Präzedenzsuche', role: 'Autopilot.Read', request: KnowledgeSearchRequest, response: KnowledgeSearchResponse },
    { method: 'GET', path: '/sources/{id}/versions/{version}', summary: 'Quelleninhalt einer Version', role: 'Autopilot.Read', response: SourceVersion },
    { method: 'GET', path: '/agents', summary: 'Bereitgestellte Bearbeiterprofile', role: 'Autopilot.Read', response: z.object({ items: z.array(NidusAgent) }) },
    { method: 'POST', path: '/agents', summary: 'Dediziertes Profil anlegen (idempotent)', role: 'Autopilot.ManageProfiles', idempotent: true, request: CreateAgentRequest, response: NidusAgent, status: 201 },
    { method: 'PATCH', path: '/agents/{id}', summary: 'Profil ändern (rowVersion)', role: 'Autopilot.ManageProfiles', request: UpdateAgentRequest, response: NidusAgent },
    { method: 'GET', path: '/companies/metrics', summary: 'Backlog je Gesellschaft; CSAT NOT_IMPLEMENTED', role: 'Autopilot.Read', response: CompanyMetrics },
    { method: 'PUT', path: '/companies/{id}/automation-policy', summary: 'Policy-Spiegel (CAS)', role: 'Autopilot.ManagePolicies', idempotent: true, request: AutomationPolicyMirror, response: AutomationPolicyState },
    { method: 'GET', path: '/companies/{id}/automation-policy', summary: 'Bestätigter Policy-Stand', role: 'Autopilot.Read', response: AutomationPolicyState.nullable() },
    { method: 'PUT', path: '/execution-gate', summary: 'Globales Gate (CAS)', role: 'Autopilot.ManagePolicies', idempotent: true, request: ExecutionGate, response: ExecutionGateState },
    { method: 'GET', path: '/execution-gate', summary: 'Bestätigter Gate-Stand', role: 'Autopilot.Read', response: ExecutionGateState },
    { method: 'PUT', path: '/exclusion-policy', summary: 'Negativliste spiegeln (CAS)', role: 'Autopilot.ManagePolicies', idempotent: true, request: ExclusionPolicy, response: ExclusionPolicyState },
    /** null = in Nidus noch nie gespiegelt (gilt als Version 0); kein 404. */
    { method: 'GET', path: '/exclusion-policy', summary: 'Bestätigter Negativlisten-Stand (null = nie gespiegelt)', role: 'Autopilot.Read', response: ExclusionPolicyState.nullable() },
    { method: 'POST', path: '/tickets/{id}/resolve-operations', summary: 'Antworten und lösen (Phase A synchron, B/C asynchron)', role: 'Autopilot.Execute', idempotent: true, request: ResolveOperationRequest, response: ResolveOperation, status: 202 },
    { method: 'GET', path: '/resolve-operations/{id}', summary: 'Operationsergebnis', role: 'Autopilot.Execute', response: ResolveOperation },
    { method: 'GET', path: '/resolve-operations/by-key/{key}', summary: 'Abgleich nach Timeout', role: 'Autopilot.Execute', response: ResolveOperation },
];
//# sourceMappingURL=nidus-integration.js.map